/* Global imports */
import {lazy, Suspense, useEffect, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {Route, Router, Switch} from 'react-router-dom';
import {Provider, useDispatch, useSelector} from 'react-redux';
import {toast, ToastContainer} from 'react-toastify';
import {ModalProvider} from 'react-modal-hook';
import {TransitionGroup} from 'react-transition-group';

/* Local imports */
import urls from 'urls';
import {history} from './history';
import {store} from '~/CreateFabricGreetingCard/reducers/store';
import {useCurrentRoute, setupWebviewNavigation} from './navigation';
import {PROPAGATE_STEP_CHANGE, SET_ALL_ASSETS} from './reducers/constants';
import {INIT_APP} from '~/common/fabric/constants';
import {fetchInvitationSummary} from '~/ViewInvitation/reducers/apiActions';
import {InjectableFooter} from '~library/organisms/InjectableFooter';
import {LoginModalReduxWrapper} from '~/common/fabric/Card/components/LoginModal/LoginModalReduxWrapper';
import {NavHeader} from '~/common/fabric/Card/components/NavHeader/NavHeader';
import {
  eventInEditMode,
  eventInUpsellMode,
  eventIsPremium,
} from '~/CreateFabricGreetingCard/reducers/details/selectors';
import {CardCreateDesignContainer} from '~/CreateFabricGreetingCard/routes/CardCreateDesign/CardCreateDesign';
import {RecipientsMessage} from './routes/RecipientsMessage/RecipientsMessage';
import {EGiftCard} from './routes/EGiftCard/EGiftCard';
import * as guestsAPI from '~/common/fabric/EventGuests/api/guests';
import {pushToDataLayer} from '~/CreateFabricGreetingCard/utils/dataTracking';
import {Review} from './routes/Review/Review';
import {UPDATE_GUESTS} from '~/common/fabric/EventGuests/reducers/guests/constants';
import {Success} from '~/CreateFabricGreetingCard/routes/Success/Success';
import {selectHasGiftCard} from '~/CreateFabricGreetingCard/reducers/eviteEvent/selectors';
import {
  INIT_PRODUCT_CONFIG_ID,
  LOAD_SELECTED_E_GIFT_CARD,
  selectUserOrSystemDisabled,
} from '~/common/fabric/GiftCard/reducers/eGiftCard';
import {setEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers';
import {stateToBackendEventRepresentation} from '~/CreateFabricGreetingCard/api/event';
import {getRetailers} from './api/egift';

import 'react-toastify/dist/ReactToastify.min.css';
import 'flatpickr/dist/themes/material_green.css';
import './app.sass';

const EventGuests = lazy(() =>
  import('~/common/fabric/EventGuests/EventGuests').then((module) => ({
    default: module.EventGuests,
  }))
);

store.dispatch({
  type: INIT_APP,
  payload: {
    event: window.evite_event,
    template: window.fabricTemplate,
    stamps: [],
    stickers: [],
    liners: [],
    envelopes: [],
    backgrounds: [],
    user: {
      userId: window.user_id || '',
      userName: window.userName || '',
      email: window.email || '',
      subscriptionPlanName: window.subscriptionPlanName || '',
      subscriptionEndDate: window.client_data?.subscription_end_date || '',
      subscriptionStatus: window.client_data?.subscription_status || '',
      avatarDisk: window.user_avatar_disk || '',
      imgUrl: window.user_img_url || '',
      initials: window.user_initials || '',
      firstName: window.firstName,
      isAuthenticated: window.isAuthenticated,
      phoneNumber: window.user_phone_number,
    },
    category_event_type: window.category_event_type,
    category_id: new URLSearchParams(window.location.search).get('c'),
    recipientsMessage: {
      globalMessage: window.evite_event?.options?.write?.text_layer_back || '',
    },
    isGiphyFeatureSwitchEnabled: window.isGiphyFeatureSwitchEnabled,
  },
});

if (window.evite_event) {
  store.dispatch(fetchInvitationSummary(window.evite_event.id));
  store.dispatch(guestsAPI.fetchGuestsListThunk(null, true));
}

setupWebviewNavigation(store);

const TrackRouteChanges = ({contentPanel}) => {
  const [previouslyTrackedRoutes, setPreviouslyTrackedRoutes] = useState([]);
  const giftCardAttached = !useSelector(selectUserOrSystemDisabled) ? 1 : 0;
  const pathObj = useCurrentRoute();
  const sections = document.getElementsByClassName('create-flow__content-section');

  for (let i = 0; i < sections.length; i++) {
    sections[i].classList.remove('active');
  }

  const activeSection = document.getElementById(pathObj.name);
  if (activeSection && contentPanel?.current) {
    activeSection.classList.add('active');
    // eslint-disable-next-line no-param-reassign
    contentPanel.current.scrollTop = activeSection.offsetTop - 120;
  }

  if (previouslyTrackedRoutes[previouslyTrackedRoutes.length - 1] !== pathObj.trackingLabel) {
    setPreviouslyTrackedRoutes((prevState) => [...prevState, pathObj.trackingLabel]);
    pushToDataLayer(pathObj.trackingLabel, true, pathObj?.trackEGift, giftCardAttached);
  }

  return null;
};

const FabricCreateGreetingCardApp = () => {
  const contentPanel = useRef(null);
  const {path, showFooter} = useCurrentRoute();
  const inUpsellMode = useSelector(eventInUpsellMode);
  const isPremium = useSelector(eventIsPremium);
  const hasGiftCard = useSelector(selectHasGiftCard);
  const inEditMode = useSelector(eventInEditMode);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchAllAssets = async () => {
      const url = urls.get('ajax_get_all_assets', {
        asset_groups: window.fabricTemplate.asset_groups,
        primary_categories: window.fabricTemplate.primary_categories,
        template: window.fabricTemplate.id,
      });
      const resp = await evite.fetch(url, {
        method: 'get',
        credentials: 'include',
        headers: {Accept: 'application/json'},
      });

      const response = await resp.json();
      const {
        stamps: allStamps,
        stickers: allStickers,
        liners: allLiners,
        backgrounds: allBackgrounds,
      } = response;
      const allEnvelopes = response.envelopes
        ? response.envelopes.filter(({blank}) => !!blank)
        : [];
      dispatch({
        type: SET_ALL_ASSETS,
        payload: {
          allAssets: {
            allStamps,
            allStickers,
            allLiners,
            allBackgrounds,
            allEnvelopes,
          },
        },
      });
    };
    fetchAllAssets().then(() => {
      store.dispatch(
        setEventToBackendSnapshot(stateToBackendEventRepresentation(store.getState()))
      );
    });
  }, [dispatch]);

  useEffect(() => {
    if (
      ['create', 'customize', 'recipients', 'egift-card', 'recipients-message', 'review'].includes(
        path
      ) &&
      !inUpsellMode
    ) {
      document.body.classList.add('no-overflow');
    }

    store.dispatch({type: PROPAGATE_STEP_CHANGE});

    return () => {
      window.scroll(0, 0);
      document.body.classList.remove('no-overflow');
    };
  }, [path]);

  useEffect(() => {
    const root = document.getElementById('fabric-create-greeting-card-root');
    root.classList.add(path);

    store.dispatch({type: PROPAGATE_STEP_CHANGE});

    if (path === 'recipients') {
      dispatch({
        type: UPDATE_GUESTS,
        payload: {hasGiftCard},
      });
    }

    return () => {
      window.scroll(0, 0);
      root.classList.remove(path);
    };
  }, [path, dispatch]);

  useEffect(() => {
    const appHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    window.addEventListener('resize', appHeight);

    appHeight();

    return () => {
      window.removeEventListener('resize', appHeight);
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: UPDATE_GUESTS,
      payload: {
        fabricGreetingCard: true,
      },
    });
  }, [dispatch]);

  useEffect(() => {
    if (window.evite_event) {
      dispatch({
        type: LOAD_SELECTED_E_GIFT_CARD,
        payload: window.evite_event.gift_card_data,
      });
    } else {
      const params = new URL(document.location.href).searchParams;
      const urlProductConfigId = params.get('product_config_id');
      if (urlProductConfigId) {
        dispatch({
          type: INIT_PRODUCT_CONFIG_ID,
          payload: urlProductConfigId,
        });
      }
    }
    dispatch(getRetailers());
  }, [dispatch]);

  return (
    <>
      <TrackRouteChanges contentPanel={contentPanel} />
      {(path !== 'preview' || !inEditMode) && <NavHeader isPremium={isPremium} />}
      <div className={`root-page-wrapper ${path}`}>
        <div className="create-flow__page" id={path}>
          <Suspense fallback={null}>
            <Switch>
              <Route
                exact
                path={['/:template/create', '/:eventId/customize']}
                component={CardCreateDesignContainer}
              />

              <Route exact path={['/:eventId/recipients']}>
                <EventGuests isQEC />
              </Route>

              <Route exact path={['/:eventId/egift-card']} component={EGiftCard} />

              <Route exact path={['/:eventId/recipients-message']} component={RecipientsMessage} />

              <Route exact path={['/:eventId/review']} component={Review} />

              <Route exact path={['/:eventId/success']} component={Success} />
            </Switch>
          </Suspense>
        </div>
      </div>
      {showFooter && <InjectableFooter />}
    </>
  );
};

const FabricCreateGreetingCardAppWrapper = () => (
  <Provider store={store}>
    <ModalProvider rootComponent={TransitionGroup}>
      <Router history={history}>
        <LoginModalReduxWrapper />
        <FabricCreateGreetingCardApp />
      </Router>

      <ToastContainer
        position="bottom-right"
        autoClose={6000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        icon={false}
      />
    </ModalProvider>
  </Provider>
);

window.toast = toast; // TODO: Remove if toasts irrelevant here.
const root = createRoot(document.getElementById('fabric-create-greeting-card-root'));
root.render(<FabricCreateGreetingCardAppWrapper />);
